/**
 * College Match — deterministic, explainable matching of a learner's report
 * matches to published college programmes. Nothing is invented: missing fees,
 * cut-offs or dates are "not verified" and never count against the learner.
 */
export type Program = {
  id: string; course: string; course_type: string | null;
  career_clusters: string[]; keywords: string[]; entrance_exams: string[];
  min_marks_percent: number | null; min_exam_score: number | null;
  fees_per_year: number | null; currency: string | null;
  source_url: string | null; verified_at: string | null;
  college: { name: string; country: string; city: string | null; website: string | null };
  deadlines: { label: string; due_date: string | null; source_url: string | null }[];
};

export type MatchInputs = {
  careers: { name: string; cluster: string }[];
  specialisations: string[];
  countries: string[];          // e.g. ["India","United States","United Kingdom"]
  cities: string[];
  marksPercent: number | null;
  examScores: Record<string, number>; // exam name -> score/percentile
  budgetPerYear: number | null;
  currency: string;
};

export type Tier = "strong" | "reach" | "explore";
export type MatchResult = {
  program: Program; tier: Tier; score: number;
  reasons: string[]; flags: string[]; aboveBudget: boolean;
  confidence: "high" | "medium" | "low"; raiseConfidence: string | null;
};

const norm = (s: string) => s.toLowerCase().trim();
const words = (s: string) => norm(s).split(/[^a-z0-9]+/).filter((w) => w.length > 2);

export function matchColleges(programs: Program[], inp: MatchInputs, limit = 9): MatchResult[] {
  const clusters = new Set(inp.careers.map((c) => norm(c.cluster)));
  const terms = new Set([...inp.specialisations, ...inp.careers.map((c) => c.name)].flatMap(words));
  const countries = new Set(inp.countries.map(norm));
  const cities = new Set(inp.cities.map(norm));

  const scored: MatchResult[] = [];
  for (const p of programs) {
    const reasons: string[] = []; const flags: string[] = [];
    const clusterHits = p.career_clusters.filter((c) => clusters.has(norm(c)));
    const kwHits = [...p.keywords, p.course].flatMap(words).filter((w) => terms.has(w));
    let fit = 0;
    if (clusterHits.length) { fit += 60; reasons.push(`Prepares you for ${clusterHits[0]}, one of your top match areas.`); }
    if (kwHits.length) { fit += Math.min(30, kwHits.length * 10); reasons.push(`Lines up with what you want to specialise in (${[...new Set(kwHits)].slice(0, 2).join(", ")}).`); }
    if (fit === 0) continue;

    // Location: soft preference
    const outsideLocation = countries.size > 0 && !countries.has(norm(p.college.country));
    if (outsideLocation) { fit -= 35; flags.push("Outside your preferred countries"); }
    if (cities.size && p.college.city && cities.has(norm(p.college.city))) { fit += 10; reasons.push(`In ${p.college.city}, a city you chose.`); }

    // Reach: compare against published cut-offs only
    let reach = 0; let unknownReach = 0;
    if (p.min_marks_percent != null) {
      if (inp.marksPercent == null) unknownReach++;
      else reach += inp.marksPercent >= p.min_marks_percent ? 1 : -1;
    } else flags.push("Marks cut-off not verified");
    if (p.min_exam_score != null && p.entrance_exams.length) {
      const exam = p.entrance_exams.find((e) => inp.examScores[e] != null);
      if (!exam) unknownReach++;
      else reach += inp.examScores[exam] >= p.min_exam_score ? 1 : -1;
    }

    let aboveBudget = false;
    if (p.fees_per_year == null) flags.push("Fees not verified");
    else if (inp.budgetPerYear != null && (p.currency ?? inp.currency) === inp.currency && p.fees_per_year > inp.budgetPerYear) {
      aboveBudget = true;
    }
    if (!p.verified_at) flags.push("Dates not verified — check official site");
    if (p.fees_per_year != null && p.currency && p.currency !== inp.currency) flags.push("Budget comparison needs a currency conversion");

    const tier: Tier = reach < 0 ? "reach" : aboveBudget || outsideLocation ? "explore" : "strong";
    const confidence = unknownReach > 0 || flags.length >= 2 ? "low" : flags.length ? "medium" : "high";
    const raiseConfidence = unknownReach > 0 ? "Add your marks and exam scores to sharpen this." : flags.length ? "We'll firm this up as official details are verified." : null;
    scored.push({ program: p, tier, score: fit + reach * 5 - (aboveBudget ? 15 : 0), reasons, flags, aboveBudget, confidence, raiseConfidence });
  }

  scored.sort((a, b) => b.score - a.score);
  const preferred = scored.filter(r => !r.flags.includes("Outside your preferred countries"));
  const candidates = preferred.length ? preferred : scored;
  // At most 2 of the same course type
  const perType = new Map<string, number>();
  const out: MatchResult[] = [];
  for (const r of candidates) {
    const t = norm(r.program.course_type || r.program.course);
    const n = perType.get(t) ?? 0;
    if (n >= 2) continue;
    perType.set(t, n + 1); out.push(r);
    if (out.length >= limit) break;
  }
  return out;
}

export type RoadmapStep = { date: string | null; title: string; detail: string; source: string | null };

/** Dated steps from today to enrolment, built only from stored deadlines. */
export function buildCollegeRoadmap(results: MatchResult[], today = new Date()): RoadmapStep[] {
  const steps: RoadmapStep[] = [{ date: today.toISOString().slice(0, 10), title: "Shortlist", detail: "Talk through these options with your family or counsellor.", source: null }];
  const exams = new Set(results.flatMap((r) => r.program.entrance_exams));
  if (exams.size) steps.push({ date: null, title: "Register for entrance exams", detail: [...exams].join(", ") + " — check each official site for registration dates.", source: null });
  const todayIso = today.toISOString().slice(0, 10);
  for (const r of results) for (const d of r.program.deadlines) {
    if (d.due_date && d.due_date < todayIso) continue;
    steps.push({ date: d.due_date, title: `${r.program.college.name}: ${d.label}`, detail: r.program.course, source: d.source_url ?? r.program.source_url });
  }
  steps.push({ date: null, title: "Prepare documents", detail: "Mark sheets, ID, photos, and statements of purpose where asked.", source: null });
  steps.push({ date: null, title: "Scholarships and enrolment", detail: "Look for scholarships alongside offers, then confirm your seat.", source: null });
  const dated = steps.filter((s) => s.date).sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""));
  return [...dated, ...steps.filter((s) => !s.date)];
}

export type SavedCollegePlan = {
  id: string; created_at: string; inputs: MatchInputs;
  results: { program_id: string; college: string; course: string; tier: Tier; reasons: string[]; flags: string[]; program?: Program; confidence?: MatchResult["confidence"]; aboveBudget?: boolean; raiseConfidence?: string | null }[];
  roadmap: RoadmapStep[];
};

export function validateCollegeInputs(inputs: MatchInputs): string | null {
  if (!inputs.specialisations.length && !inputs.careers.length) return "Add at least one study area.";
  if (inputs.marksPercent != null && (!Number.isFinite(inputs.marksPercent) || inputs.marksPercent < 0 || inputs.marksPercent > 100)) return "Marks must be between 0 and 100.";
  if (inputs.budgetPerYear != null && (!Number.isFinite(inputs.budgetPerYear) || inputs.budgetPerYear < 0)) return "Enter a valid yearly budget.";
  if (Object.values(inputs.examScores).some(v => !Number.isFinite(v) || v < 0)) return "Enter valid, non-negative exam scores.";
  return null;
}

/** Parse a simple CSV (quoted fields supported). */
export function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = []; let row: string[] = []; let cell = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; } else if (c === '"') q = false; else cell += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
    else cell += c;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  const [head, ...body] = rows.filter((r) => r.some((x) => x.trim()));
  if (!head) return [];
  const keys = head.map((h) => h.trim().toLowerCase());
  return body.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? "").trim()])));
}

/** Format a date-only value (YYYY-MM-DD) as the same calendar day in every time zone. */
export function formatDateOnly(value: string, opts?: Intl.DateTimeFormatOptions, locale?: string): string {
  const day = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
  const d = new Date(day);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString(locale, opts);
}
